const { yupResolver } = require("@hookform/resolvers/yup");
import Head from "next/head";
import dynamic from "next/dynamic";
import * as Sentry from "@sentry/nextjs";
import * as yup from "yup";
import { loadStandardContent } from "utils/api";
import Link from "next/link";
import Image from "next/image";

import { useSession } from "@/components/providers/session";
import { useEffect, useRef, useState } from "react";
import getStripe from "@/utils/stripe";
import { Disclosure, RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { currency } from "utils/formats";
import { format } from "date-fns/format";
import { useFieldArray, useForm } from "react-hook-form";
import classNames from "utils/classnames";
import cookies from "js-cookie";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CalendarIcon,
  ChevronUpIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { recordConversion, Conversion } from "@/utils/conversions";
import { DiscountCodeObject } from "interfaces";
import { capitalize } from "@/utils/words";
import { klayAction } from "@/utils/klay";
import { Appearance, StripeElements, Stripe } from "@stripe/stripe-js";
import EmptySvg from "@/components/assets/checkout-empty.svg";
import { REFERRAL_CODE_KEY } from "@/utils/referrals";
import { addDays, isAfter } from "date-fns";

type UpsellResponse = {
  condition: string;
  discountCode: string;
  discountType: string;
  discountValue: number;
  minimumQuantity?: number;
  remainingApplyFrom: string[];
  remainingApplyTo: string[];
  validAppliesFrom: string[];
  validAppliesTo: string[];
  remainingCoursesFrom?: any;
  remainingCoursesTo?: any;
  description: string | null;
  discount: DiscountCodeObject;
  loading?: boolean;
};

const Modal = dynamic(() => import("components/molecules/modal"));
const Status = dynamic(() => import("components/atoms/status"));
const Loader = dynamic(() => import("components/atoms/loader"));

const ADMIN_FEE = 10000;

const schema = yup.object().shape({
  selected: yup.string(),
  name: yup.string().when("selected", (a, schema) => {
    return a === "card" ? schema.required() : schema.optional();
  }),
  surname: yup.string().when("selected", (a, schema) => {
    return a === "card" ? schema.required() : schema.optional();
  }),
  email: yup.string().when("selected", (a, schema) => {
    return a === "card" ? schema.email().required() : schema.optional();
  }),
  phone: yup.string().when("selected", (a, schema) => {
    return a === "card" ? schema.required() : schema.optional();
  }),
  courses: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          title: yup.string(),
          price: yup.number(),
          wasPrice: yup.number(),
          date: yup.string().nullable(true),
          includeKit: yup.string(),
          freeKit: yup.boolean().default(false),
          kitPrice: yup.number(),
          kitName: yup.string(),
          kitWorthPrice: yup.number(),
          // selectedEvent: yup.object().required(),
        })
        .required()
    )
    .min(1),
});

function InnerPay({ internalRef }) {
  const elements = useElements();
  const stripe = useStripe();
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const retryDelay = 1000;

  useEffect(() => {
    if (!elements && retryCount < maxRetries) {
      console.warn(
        `Stripe elements not available, retrying (${
          retryCount + 1
        }/${maxRetries})...`
      );
      setTimeout(() => setRetryCount(retryCount + 1), retryDelay);
      return;
    }
    if (!internalRef.current.elements && elements) {
      console.log("Stripe elements initialized successfully.");
      internalRef.current.callback({
        callback: internalRef.current.callback,
        elements,
        stripe,
      });
    }
  }, [elements, stripe, retryCount, internalRef]);

  if (!elements) return <p>Loading payment form...</p>;

  return <PaymentElement />;
}

function Pay({ client_secret, internalRef }) {
  return (
    <Elements
      key={client_secret}
      stripe={getStripe()}
      options={{
        clientSecret: client_secret,
        appearance,
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css?family=Montserrat:300,300i,400,500,600",
          },
        ],
      }}
    >
      <InnerPay internalRef={internalRef} />
    </Elements>
  );
}

function buyXGetY(data, courses, discount) {
  if (!discount) {
    return data;
  }
  const { rules } = discount.config;

  //  Abort if basket does not meet the minimum quantity needed to apply the discount
  if (rules.minimumQuantity > courses.length) {
    return data;
  }

  //  if buyEventAmount === 1 method is some else every
  // Every would be applicable in case we said buy 2 or 3 and get 1 free or 2 with 50%

  // const method = rules.buyEventAmount === 1 ? "some" : "every";
  // const method = "some";
  // const basketHasRequiredCourses = courses[method]((item) => {
  //   return item.event ? rules.applyFrom.includes(item.event.id) : false;
  // });

  //If validApplyFrom is greater or equal than buyAmount and validApplyTo is greater or equal than getAmount then discount is valid
  const basketHasRequiredCourses = !!(
    discount.upsell.validAppliesFrom.length >= rules.buyEventAmount &&
    discount.upsell.validAppliesTo.length >= rules.getEventAmount
  );

  //  Abort if basket does not meet the required courses needed to apply the discount
  if (!basketHasRequiredCourses) {
    return data;
  }

  // Sort courses by price lowest to highest
  // Then filter out courses that do not match courses the discount can be applied to
  const sorted = [...courses]
    .sort((a, b) => {
      const ap = a.selectedEvent
        ? a.selectedEvent.seat_price
        : a.event.standardPrice;
      const bp = b.selectedEvent
        ? b.selectedEvent.seat_price
        : b.event.standardPrice;
      return ap - bp;
    })
    .filter((x) => {
      return x.event ? rules.applyTo.includes(x.event.id) : false;
    });

  // Create an array of the quantity of courses that can be discounted
  const res = Array(rules.getEventAmount)
    .fill(null)
    // Map over the array and return the course at the index of the current iteration
    // This will return the first x courses that can be discounted
    .map((_, i) => {
      const item = sorted[i];
      if (!item) return null;
      const price = item.selectedEvent
        ? item.selectedEvent.seat_price
        : item.event.standardPrice;
      const discountAmount =
        discount.type === "percent"
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...item,
        discountAmount,
      };
    })
    .filter(Boolean);

  data.discount = res.reduce((a, b) => a + b.discountAmount, 0);

  // data.total = data.total - data.discount;
  return {
    ...data,
    courses: res,
  };
}

function bulk(data, courses, discount) {
  if (!discount) {
    return data;
  }
  const { rules } = discount.config;

  //  Abort if basket does not meet the minimum quantity needed to apply the discount
  if (rules.minimumQuantity > courses.length) {
    return data;
  }

  const items =
    discount.code === "JUNEBUG"
      ? courses.filter((x) => {
          return (
            x.event &&
            x.selectedEvent &&
            new Date(x.selectedEvent.start) >= new Date() &&
            new Date(x.selectedEvent.start) < new Date("2025-07-01")
          );
        })
      : discount.code === "SPRING50" || discount.code === "SPRING30"
      ? courses.filter((x) => {
          console.log("X1", x);
          console.log(
            "X1",
            x.event &&
              ((x.selectedEvent &&
                new Date(x.selectedEvent.start) >= new Date("2025-05-01") &&
                new Date(x.selectedEvent.start) < new Date("2025-06-01")) ||
                (x.selectedEvent &&
                  new Date(x.selectedEvent.start) >= new Date("2025-04-01") &&
                  new Date(x.selectedEvent.start) < new Date("2025-05-01")))
          );
          return (
            x.event &&
            ((x.selectedEvent &&
              new Date(x.selectedEvent.start) >= new Date("2025-05-01") &&
              new Date(x.selectedEvent.start) < new Date("2025-06-01")) ||
              (x.selectedEvent &&
                new Date(x.selectedEvent.start) >= new Date("2025-04-01") &&
                new Date(x.selectedEvent.start) < new Date("2025-05-01")))
          );
        })
      : courses;

  const processedItems = items
    .filter((x) => {
      return x.event
        ? discount.events &&
            discount.events.length > 0 &&
            discount.events.find((y) => y.event_detail_id === x.event.id)
        : false;
    })
    .map((x) => {
      const price = x.selectedEvent
        ? x.selectedEvent.seat_price
        : x.event.standardPrice;
      const discountAmount =
        discount.type === "percent"
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...x,
        discountAmount,
      };
    });

  data.discount = processedItems.reduce((a, b) => a + b.discountAmount, 0);

  // data.total = data.total - data.discount;
  return {
    ...data,
    courses: processedItems,
  };
}

function validUntil(data, courses, discount) {
  if (!discount) {
    return data;
  }

  const { rules } = discount.config;

  //  Abort if basket does not meet the minimum quantity needed to apply the discount
  if (rules.minimumQuantity > courses.length) {
    return data;
  }

  const items = courses
    .filter((x) => {
      return x.selectedEvent
        ? discount.validAppliesTo.includes(x.selectedEvent.id)
        : false;
    })
    .map((x) => {
      const price = x.selectedEvent
        ? x.selectedEvent.seat_price
        : x.event.standardPrice;

      const discountAmount =
        discount.type === "percent"
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...x,
        discountAmount,
      };
    });

  data.discount = items.reduce((a, b) => a + b.discountAmount, 0);

  // data.total = data.total - data.discount;
  return {
    ...data,
    courses: items,
  };
}

const BasketState = ({
  mode,
  courses,
  code,
  discount,
  points,
}): {
  total: number;
  deposit: number;
  fullTotal: number;
  vat: number;
  discount: any;
  points: number;
  courses?: any;
} => {
  let data = {
    total: 0,
    deposit: 0,
    fullTotal: 0,
    points: 0,
    vat: 0,
    discount: null,
  };

  if (courses && courses.length > 0) {
    data.total = courses.reduce((acc, course) => {
      if (!course.event) return acc;
      const pricingItem =
        course &&
        course.fields &&
        course.fields.content.find(
          (x) => x.sys.contentType.sys.id === "pricing"
        );

      const kitPrice = pricingItem?.fields?.kitPrice
        ? pricingItem?.fields?.kitPrice * 100
        : 0;

      const seat_price = course.selectedEvent
        ? course.selectedEvent.seat_price
        : course.event
        ? course.event.standardPrice
        : 0;

      if (course.includeKit && kitPrice > 0) {
        return acc + seat_price + kitPrice;
      }

      return acc + seat_price;
    }, 0);

    data.total = data.total - points;
  }

  if (discount && discount.code === "CPD999" && courses.length === 3) {
    const ct = courses.reduce((a, b) => {
      if (b.event.package) {
        return a;
      }

      if (b.fields.categories.includes("vtct")) {
        return a;
      }

      return a + 1;
    }, 0);

    if (ct === 3) {
      data.total = 99900;
    }
  } else if (
    discount &&
    discount.code !== "VATFREE" &&
    discount.code !== "2025"
  ) {
    const { config } = discount;
    if (config && config.condition === "buyXGetY") {
      data = buyXGetY(data, courses, discount);
    }

    if (config && config.condition === "bulkDiscount") {
      data = bulk(data, courses, discount);
    }

    if (config && config.condition === "validUntil") {
      data = validUntil(data, courses, discount);
    }
  }

  data.discount = Math.round(data.discount);

  if (data.discount && data.discount > 0) {
    data.total = data.total - data.discount;
  }

  data.vat =
    discount &&
    (discount.code === "VATFREE" || discount.code === "2025") &&
    discount.valid
      ? 0
      : Math.round(0.2 * data.total);
  data.fullTotal = Math.round(data.total + data.vat);

  if (data.fullTotal > 0) {
    const percent = data.fullTotal * 0.1;
    data.deposit = Math.round(percent > ADMIN_FEE ? percent : ADMIN_FEE);
  }

  return data;
};

function DiscountCode({
  discount,
  setDiscount,
  setUpsell,
  courses,
  code,
  inputRef,
  submitRef,
}) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    length: courses.length,
  });

  useEffect(() => {
    if (discount && courses.length !== state.length) {
      handleClick(code);
      setState((s) => ({ ...s, length: courses.length }));
    }
  }, [courses.length]);

  const handleClick = async (code: string = null) => {
    const el = document.getElementById("discount-code") as HTMLInputElement;
    const val = code ? code : el ? el.value : "";

    if (val && val.length > 0) {
      try {
        setState((s) => ({ ...s, loading: true }));
        const res = await fetch("/api/discount-code/", {
          method: "POST",
          body: JSON.stringify({
            code: val.replace(/ /g, "").toUpperCase(),
            courses: courses,
            selectedCourses: courses.map((x) => x.selectedEvent),
          }),
        });

        const data = await res.json();

        if (data && data.code && data.valid && data.active) {
          setState((s) => ({ ...s, loading: false, error: null }));
          setDiscount(data);
          if (data.upsell) {
            setUpsell(data.upsell);
          }
        } else {
          setDiscount(null);
          setUpsell(null);
          setState((s) => ({
            ...s,
            loading: false,
            error: data.error || "Invalid code. Please try again.",
          }));
        }

        //
      } catch (err) {
        console.log(err);
        setDiscount(null);
        setUpsell(null);
        setState((s) => ({
          ...s,
          loading: false,
          error: "Error checking your code.",
        }));
        Sentry.captureException(err);
      }
    }
  };

  return (
    <div className="pt-4 border-t border-white border-opacity-50 relative">
      <label
        htmlFor="discount-code"
        className="block text-sm font-medium text-white"
      >
        Discount code
      </label>
      <div className="mt-1 flex space-x-4 items-start">
        <div className="flex-1">
          <input
            type="text"
            id="discount-code"
            name="discount-code"
            ref={inputRef}
            defaultValue={code}
            placeholder="Have a code? Enter it here"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm text-gray-500"
          />
          {state.error && (
            <span className="text-red-700 text-sm">{state.error}</span>
          )}
        </div>
        <div
          ref={submitRef}
          aria-label="Apply discount code"
          onClick={() => handleClick()}
          className="cursor-pointer rounded-md bg-gray-100 py-2 px-4 text-sm font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50 transition-colors duration-100"
        >
          Apply
        </div>
      </div>
      {state.loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-brand-500 bg-opacity-50">
          <svg
            viewBox="0 0 44 44"
            width="44"
            height="44"
            fill="#fff"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#fff"
          >
            <g fill="none" fillRule="evenodd" strokeWidth="2">
              <circle cx="22" cy="22" r="1">
                <animate
                  attributeName="r"
                  begin="0s"
                  dur="1.8s"
                  values="1; 20"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.165, 0.84, 0.44, 1"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  begin="0s"
                  dur="1.8s"
                  values="1; 0"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.3, 0.61, 0.355, 1"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="22" cy="22" r="1">
                <animate
                  attributeName="r"
                  begin="-0.9s"
                  dur="1.8s"
                  values="1; 20"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.165, 0.84, 0.44, 1"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  begin="-0.9s"
                  dur="1.8s"
                  values="1; 0"
                  calcMode="spline"
                  keyTimes="0; 1"
                  keySplines="0.3, 0.61, 0.355, 1"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          </svg>
        </div>
      )}
    </div>
  );
}

function YourRewards({ setPoints, points, show }) {
  const handleClick = () => {
    setPoints(counter);
  };

  const [counter, setCounter] = useState(0);
  if (!show) return null;

  return (
    <div className="pt-4 mt-4 border-t border-white border-opacity-50 relative">
      <label
        htmlFor="points-input"
        className="block text-sm font-medium text-white pb-1"
      >
        You have {points} points to spend
      </label>
      <div className="flex items-center gap-2.5">
        <div className="bg-white w-full pr-2 flex items-center rounded-md border border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm text-gray-500">
          <input
            type="number"
            id="reward-input"
            min={0}
            max={points}
            onChange={(e) => {
              if (e.target.valueAsNumber <= points) {
                setCounter(e.target.valueAsNumber);
              } else {
                setCounter(0);
              }
            }}
            className="h-9 text-left bg-transparent rounded-l-md text-gray-900 border-0 ring-0 focus:outline-none focus:border-transparent focus:ring-transparent text-sm block w-full py-2.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder="Enter number of points to spend"
            value={counter}
            required
          />
          <div className="flex flex-row gap-1.5">
            <button
              type="button"
              id="decrement-button"
              onClick={() => {
                if (counter > 0) {
                  setCounter(counter - 1);
                }
              }}
              className="w-6 h-6 flex items-center rounded-md justify-center focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <MinusIcon strokeWidth="2" className="w-4 h-4 text-gray-600" />
            </button>
            <button
              type="button"
              id="increment-button"
              onClick={() => {
                if (counter < points) {
                  setCounter(counter + 1);
                }
              }}
              className="w-6 h-6 flex items-center rounded-md justify-center focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <PlusIcon strokeWidth="2" className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
        <div
          onClick={() => handleClick()}
          className="cursor-pointer w-24 rounded-md bg-gray-100 py-2 px-4 text-sm font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50 transition-colors duration-100"
        >
          Redeem
        </div>
      </div>
      <div className="mt-2 flex justify-between items-center">
        <p id="reward-text-explanation" className="text-xs text-white">
          This purchase will use {counter} points = £
          {(counter / 100).toFixed(2)}
        </p>
      </div>
    </div>
  );
}

function Upsell({
  data,
  checkUpsell,
  discountValue,
  courses,
  inputRef,
  submitRef,
  setSearching,
  setUpsellMode,
}: {
  data: UpsellResponse;
  checkUpsell: (course, code: string) => void;
  discountValue: number;
  courses: any;
  inputRef: React.MutableRefObject<HTMLInputElement>;
  submitRef: React.MutableRefObject<HTMLSpanElement>;
  setSearching: any;
  setUpsellMode: any;
}) {
  const needsAdditional =
    data.condition === "bulkDiscount" &&
    data.minimumQuantity &&
    data.minimumQuantity > 0 &&
    data.minimumQuantity - courses.length > 0;

  const formattedDiscountIntro = needsAdditional
    ? `Add ${
        data.minimumQuantity - courses.length
      } more courses - with discount code`
    : "Use discount code";

  return !discountValue &&
    ((data.remainingCoursesFrom && data.remainingCoursesFrom.length > 0) ||
      (data.remainingCoursesTo && data.remainingCoursesTo.length > 0)) ? (
    <div className="mt-6 flex-col items-center justify-between text-gray-700 bg-white rounded-lg shadow py-3 px-4">
      <div className="flex-col ">
        {data.discount && data.discount.description ? (
          <div className="flex flex-auto flex-col ">
            <p className="text-sm font-medium text-gray-700">
              {formattedDiscountIntro}{" "}
              <span className="font-semibold">
                &quot;
                {data.discountCode}&quot;
              </span>{" "}
              for:
            </p>
            <p className="text-sm text-gray-500">{data.discount.description}</p>
          </div>
        ) : data.discountCode && data.discountValue && data.discountType ? (
          <p className="text-sm font-medium text-gray-700">
            {formattedDiscountIntro} &quot;
            <span
              className="font-semibold cursor-pointer"
              title="Apply discount code"
              aria-label="Apply discount code"
              onClick={() => {
                if (!inputRef.current || !submitRef.current) {
                  return;
                }
                inputRef.current.value = data.discountCode;
                submitRef.current.click();
              }}
            >
              {data.discountCode}
            </span>
            &quot; for{" "}
            {data.discountType === "percent"
              ? `${data.discountValue}%`
              : `${currency(data.discountValue)}`}{" "}
            off your training.
          </p>
        ) : (
          <div className="flex flex-auto flex-col mb-2">
            <p className="text-sm font-medium text-gray-700">
              Take Your Training Futher!
            </p>
          </div>
        )}
        <p className="text-gray-600 text-xs">{data.description}</p>
        <div className="py-1">
          {data.remainingCoursesFrom &&
            data.remainingCoursesFrom.length > 0 && (
              <>
                <p className="text-gray-700 text-sm my-1">Courses for offer:</p>
                {data.remainingCoursesFrom
                  .filter(
                    (x) =>
                      !courses
                        .map((obj) => obj.event && obj.event.id)
                        .includes(x.event && x.event.id)
                  )
                  .map((course, idx) => {
                    return (
                      <UpsellItem
                        key={idx}
                        course={course}
                        checkUpsell={() =>
                          checkUpsell(course, data.discountCode)
                        }
                      />
                    );
                  })}
              </>
            )}
          {data.remainingCoursesTo && data.remainingCoursesTo.length > 0 && (
            <>
              <p className="text-gray-700 text-sm mb-1 mt-2">
                Offer Applies To:
              </p>
              {data.remainingCoursesTo
                .filter(
                  (x) =>
                    !courses
                      .map((obj) => obj.event.id)
                      .includes(x.event && x.event.id)
                )
                .map((course, idx) => {
                  const discountAmount =
                    data.discountType === "percent"
                      ? (data.discountValue / 100) * course.event.standardPrice
                      : data.discountValue;

                  return (
                    <UpsellItem
                      key={idx}
                      course={course}
                      checkUpsell={() => checkUpsell(course, data.discountCode)}
                      discountValue={discountAmount}
                    />
                  );
                })}
            </>
          )}

          <button
            type="button"
            onClick={() => {
              setUpsellMode(true);
              setSearching(true);
            }}
            className="mx-auto mt-4 rounded-md bg-brand-500 py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50 transition-colors duration-100 flex items-center justify-center"
          >
            All discount deal options
          </button>
        </div>
      </div>
    </div>
  ) : (
    <>
      {data.loading && !data.discount ? (
        <div className="mt-6 flex items-center justify-center text-gray-700 bg-white rounded-lg shadow py-3 px-4">
          <p className="text-sm font-medium text-gray-700">
            Checking for eligible offers
          </p>
          <Loader styles="px-4 " />
        </div>
      ) : null}
    </>
  );
}

function UpsellItem({ course, checkUpsell, discountValue = undefined }) {
  const img =
    course.fields && course.fields.listingImage
      ? course.fields.listingImage.fields.file.url
      : null;

  if (!course.fields) {
    return null;
  }

  return (
    <div className="flex items-center justify-between mb-1 w-full">
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
        {img && (
          <img
            src={img}
            alt={course.fields.title}
            className="h-full w-full object-cover object-center"
          />
        )}
      </div>
      <div className="flex-col w-full ml-2">
        <div className="flex justify-between w-full items-center">
          <p className="text-gray-600 text-sm font-medium">
            {course.fields.title}
          </p>
          <div
            className="bg-brand-500 rounded text-xs font-medium text-white px-2 py-1 cursor-pointer justify-start"
            onClick={checkUpsell}
          >
            Add
          </div>
        </div>
        {!!discountValue ? (
          <div className="flex items-center space-x-2">
            <s className="text-xs font-medium text-gray-500 tabular-nums">
              {currency(course.event.standardPrice)}
            </s>
            <p className="text-xs -mt-0.5 tabular-nums font-medium text-gray-500">
              {currency(course.event.standardPrice - discountValue)}
            </p>
          </div>
        ) : (
          <p className="text-xs -mt-0.5 tabular-nums font-medium text-gray-500">
            {currency(course.event.standardPrice)}
          </p>
        )}
      </div>
    </div>
  );
}

function CourseItem({ course, update, remove, discountedItem, form, index }) {
  if (!course.event) return null;
  const img =
    course.fields && course.fields.listingImage
      ? course.fields.listingImage.fields.file.url
      : null;

  const price = course.selectedEvent
    ? course.selectedEvent.seat_price
    : course.event
    ? course.event.standardPrice
    : 0;

  const displayPrice = discountedItem
    ? price - discountedItem.discountAmount
    : price;

  console.log("discountedItem", discountedItem);
  const pricingItem =
    course &&
    course.fields &&
    course.fields.content.find((x) => x.sys.contentType.sys.id === "pricing");

  const eventList =
    course &&
    course.event &&
    course.event.events &&
    course.event.events.length > 0 &&
    course.event.events.filter((course) =>
      course.countStudentRegistrations &&
      course.countStudentRegistrations.aggregate
        ? course.seats - course.countStudentRegistrations.aggregate.count > 0
        : false
    );

  return (
    <li key={course.id} className="flex-col">
      <div className={classNames("py-6 flex items-start space-x-4")}>
        <Link href={course.fields.url} className="shrink-0">
          <img
            src={img}
            alt={course.fields.title}
            className="h-20 w-20 flex-none rounded-md object-cover object-center"
          />
        </Link>
        <div className="flex-auto space-y-1">
          <Link href={course.fields.url} className="text-white">
            {course.fields.title}
          </Link>
          <p>{course.color}</p>

          {course.event.package ? (
            <p className="text-white text-xs font-semibold pt-1">
              Schedule your course dates after completing purchase
            </p>
          ) : (
            <div
              className={classNames(
                eventList && eventList.length > 0 ? "lg:pr-6" : ""
              )}
            >
              {eventList && eventList.length > 0 ? (
                <select
                  name="course_date"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm text-gray-600 p-1.5 px-2"
                  onChange={(e) => {
                    const item = course.event.events.find(
                      (x) => x.id === e.currentTarget.value
                    );
                    if (item) {
                      update({ ...course, selectedEvent: item });
                    }
                  }}
                  value={course.selectedEvent ? course.selectedEvent.id : ""}
                >
                  <option value={null} disabled={course.selectedEvent !== null}>
                    Please Select a Date
                  </option>
                  {course.event.events &&
                    course.event.events.length > 0 &&
                    course.event.events
                      .filter((course) =>
                        course.countStudentRegistrations &&
                        course.countStudentRegistrations.aggregate
                          ? course.seats -
                              course.countStudentRegistrations.aggregate.count >
                            0
                          : false
                      )
                      .map((event, i) => (
                        <option
                          key={`${course.id}-${event.id}`}
                          value={event.id}
                        >
                          {format(new Date(event.date), "do MMMM yyyy")} -{" "}
                          {currency(event.seat_price)}
                        </option>
                      ))}
                </select>
              ) : course.fields.data && course.fields.data.hybrid ? (
                <p className="text-white text-xs font-semibold pt-1">
                  This is a hybrid course - complete the online portion first
                  with immediate access and then attend practical training
                </p>
              ) : (
                <p className="text-white text-xs font-semibold pt-1">
                  Upcoming dates not currenly scheduled. Book now to schedule a
                  new date for this course
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col h-20 text-right">
          {discountedItem && (
            <s className="text-xs font-medium text-white tabular-nums">
              {currency(price)}
            </s>
          )}
          <p className="text-base font-medium text-white tabular-nums flex-1">
            {currency(displayPrice)}
          </p>
          <button
            type="button"
            className="border-none text-gray-100 float-right text-right p-0 hover:text-white flex justify-end transition-colors duration-100"
            onClick={remove}
          >
            <TrashIcon className="w-4" />
          </button>
        </div>
      </div>
      {/* {pricingItem && pricingItem.fields && pricingItem.fields.kitName ? (
        <div className=" md:flex items-center text-white">
          {pricingItem.fields.freeKit && (
            <>
              <div className="py-3">
                <label className="md:flex-1">
                  Includes a{" "}
                  <span className="text-gray-600 font-semibold">FREE</span>{" "}
                  {pricingItem.fields.kitName}{" "}
                  {pricingItem.fields.kitWorthPrice
                    ? `(worth £${pricingItem.fields.kitWorthPrice}!)`
                    : ``}
                </label>
              </div>
            </>
          )}
          {!pricingItem.fields.freeKit && (
            <div className="py-3">
              <label className="">
                Include a {pricingItem.fields.kitName}{" "}
                {pricingItem.fields.kitWorthPrice
                  ? `(worth £${pricingItem.fields.kitWorthPrice}!)`
                  : ``}
              </label>
              <input
                type="checkbox"
                {...form.register(`courses.${index}.includeKit`)}
                className="h-5 w-5 ml-2 lg:text-brand-600 text-brand-500 focus:ring-brand-500 border-gray-300 rounded"
              />
            </div>
          )}
        </div>
      ) : null} */}
    </li>
  );
}

type StripeRefParams = {
  elements: StripeElements | null;
  stripe: Stripe | null;
};

type RefParams = {
  callback: (params: RefParams) => void;
} & StripeRefParams;

export default function NewBook(props) {
  const firstRenderRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLSpanElement>(null);
  const session = useSession();

  const [intentState, setIntentState] = useState({
    client_secret: null,
    id: null,
  });
  const [mode, setMode] = useState("full");
  const [searching, setSearching] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [code, setCode] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [referral, setReferral] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [upsellMode, setUpsellMode] = useState(false);
  const [error, setError] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [klaviyoEvent, setKlaviyoEvent] = useState(false);
  const [klayEventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutState, setCheckoutState] = useState({
    loading: false,
    success: false,
    points: 0,
  });

  const [finance, setFinance] = useState({
    finance: false,
    done: false,
  });

  const getSession = () => {
    if (typeof localStorage !== "undefined") {
      const item = localStorage.getItem(REFERRAL_CODE_KEY);

      if (item) {
        return item;
      } else {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    async function fetchData() {
      const session = getSession();

      if (session) {
        const ref = await fetch("/api/referral", {
          method: "post",
          body: JSON.stringify({
            code: session,
          }),
        }).then(async (res) => res.json());
        if (ref?.id) {
          setReferral(ref);
        }
      }
    }
    fetchData();
  }, []);

  const internalRef = useRef<
    {
      callback: (params: RefParams) => void;
    } & RefParams
  >({
    elements: null,
    stripe: null,
    callback: (el) => {
      internalRef.current = {
        ...el,
      };
      const paymentElement = el.elements.getElement("payment");
      if (paymentElement) {
        paymentElement.on("change", (event) => {
          setMode((s) =>
            event.value.type === "klarna"
              ? "full"
              : event.value.type === "afterpay_clearpay"
              ? "full"
              : s
              ? s
              : "fee"
          );
          try {
            window.gtag &&
              window.gtag("event", "set_checkout_option", {
                checkout_step: 3,
                checkout_option: "payment method",
                value:
                  event.value.type === "klarna"
                    ? "klarna"
                    : event.value.type === "afterpay_clearpay"
                    ? "afterpay"
                    : "card",
              });
          } catch (err) {
            console.log(err);
            Sentry.captureException(err);
          }
        });
        // setState((s) => ({ ...s, paymentElement }));
      }
    },
  });

  const financeSubmit = async (data) => {
    const courses = courseFields.fields.map((x) => {
      return { id: x.event.id, title: x.fields.title };
    });
    try {
      setError(null);

      const req = await fetch("/api/callback/", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          courses,
          type: "finance",
        }),
      });

      const res = await req.json();

      if (res && res.success) {
        setFinance((s) => ({ ...s, finance: true, done: true }));

        try {
          recordConversion({
            send_to: Conversion.callback,
          });

          (window as any).fbq && (window as any).fbq("track", "Contact");

          (window as any).heap &&
            (window as any).heap.addUserProperties({
              ...data,
            });

          (window as any).heap &&
            (window as any).heap.track("Finance Request", {
              ...data,
            });

          // @ts-ignore
          klaviyo &&
            // @ts-ignore
            klaviyo.push([
              "identify",
              {
                $email: data.email,
                $first_name: data.first_name,
                $last_name: data.last_name,
                $phone_number: data.phone_number,
              },
            ]);

          // @ts-ignore
          klaviyo &&
            // @ts-ignore
            klaviyo.push([
              "track",
              "Finance Request",
              {
                $email: data.email,
                $first_name: data.first_name,
                $last_name: data.last_name,
                $phone_number: data.phone_number,
                $path_name: window.location.pathname,
              },
            ]);
        } catch (err) {
          console.log(err);
        }
      } else {
        setError("Error processing request, Please try again");
      }
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
      setError("Error processing request, Please try again");
    }
  };

  const financeForm = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    },
  });

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      selected: "card",
      name: "",
      surname: "",
      email: "",
      phone: "",
      courses: [],
    },
  });

  const courseFields = useFieldArray({
    control: form.control,
    name: "courses",
  });

  const currentCourses = form.watch("courses");

  const basketData = BasketState({
    mode,
    courses: currentCourses,
    code,
    discount,
    points: checkoutState.points,
  });

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      const params = new URLSearchParams(window.location.search);
      if (params) {
        const status = params.get("status");
        const code = params.get("code");
        if (code) {
          setCode(code);
        } else {
          setLoading(false);
        }
        if (status && status === "failed") {
          setError("Payment failed. Please try an alternative payment method");
        }
      }
      createIntent(ADMIN_FEE)
        .then((r) => setIntentState((s) => ({ ...s, ...r })))
        .catch((err) => {
          Sentry.captureException(err);
          console.log(err);
        });
    }
  }, []);

  useEffect(() => {
    if (session.selectedCourses.length > 0 && !klaviyoEvent) {
      try {
        const items = session.selectedCourses.map((x) => klayAction(x));
        const eventId =
          session.user && session.user.attributes
            ? `${session.user.attributes.sub.substring(0, 7)}_${Date.now()}`
            : `${Math.floor(1000000 + Math.random() * 900000)}_${Date.now()}`;

        setEventId(eventId);

        const payload = {
          $event_id: eventId,
          $value: session.selectedCourses.reduce(
            (a, x) =>
              a +
              (x.package
                ? x.event.standardPrice / 100
                : x.selectedEvent
                ? x.selectedEvent.price / 100
                : x.event.standardPrice / 100),
            0
          ),
          ItemNames: items.map((x) => x.ProductName),
          CheckoutURL: "https://www.cosmetic.college/enroll/",
          Categories: items.map((p) => p.Categories).flat(),
          Items: items,
        };
        setKlaviyoEvent(true);
        // @ts-ignore
        klaviyo && klaviyo.push(["track", "Started Checkout", payload]);
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }
    }
  }, [session.selectedCourses]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramCode = params.get("code");
    if (paramCode && paramCode === code) {
      getDiscountFromParams(code);
    }
  }, [code]);

  useEffect(() => {
    const { total } = BasketState({
      mode,
      courses: currentCourses,
      code,
      discount,
      points: checkoutState.points,
    });

    if (discount && discount.code) {
      const discountobj = {
        item_id: discount.id,
        code: discount.code,
        value: discount.value,
        currency: "GBP",
        type: discount.type,
        affiliation: "Cosmetic College",
        item_brand: "Cosmetic College",
        item_category: "Courses",
        quantity: 1,
      };
      try {
        window.gtag &&
          window.gtag("event", "discount_code_added", {
            currency: "GBP",
            checkoutTotal: total / 100,
            discount: discountobj,
          });

        klaviyoAnalytics(discountobj, null, "Discount Applied");
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }
    } else {
      try {
        window.gtag &&
          window.gtag("event", "discount_code_removed", {
            currency: "GBP",
            checkoutTotal: total / 100,
            discount: null,
          });
        klaviyoAnalytics({ discount: null }, null, "Discount Removed");
      } catch (err) {
        console.log(err);
      }
    }
  }, [discount]);

  const getDiscountFromParams = async (code: string) => {
    const courses = courseFields.fields.map((x) => x.event.id);

    if (code && code.length > 0) {
      try {
        const res = await fetch("/api/discount-code/", {
          method: "POST",
          body: JSON.stringify({
            code: code.replace(/ /g, "").toUpperCase(),
            courses,
          }),
        });

        const data = await res.json();

        if (data && data.upsell && data.valid && data.active) {
          setDiscount(data);
          if (data.upsell) {
            data.upsell.description = data.description;
            setUpsell(data.upsell);

            if (data.config.condition === "buyXGetY") {
              await addCoursesToBasket([
                ...data.upsell.remainingApplyFrom,
                ...data.upsell.remainingApplyTo,
              ]);
            } else if (data.config.condition === "bulkDiscount") {
              setBulk(true);
            }
          }
        } else {
          setDiscount(null);
          setUpsell(null);
          setError("Invalid code. Please try again.");
        }
      } catch (err) {
        console.log(err);
        setDiscount(null);
        setUpsell(null);
        Sentry.captureException(err);
      }
    }
    setLoading(false);
  };

  const addCoursesToBasket = async (courses: string[]) => {
    await Promise.all(
      courses.map(async (course) => await handleAddCourse({ id: course }))
    );
    await checkDiscount(code, courses);
  };

  const getUpsellOnLoad = async (ids: string[]) => {
    try {
      setUpsell((s) => ({ ...s, loading: true }));
      const courseResult = await getUpsellInformation(ids);
      setUpsell((s) => ({ ...s, loading: false }));

      if (courseResult) {
        setUpsell(courseResult);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const courses = courseFields.fields;
    courseFields.remove();
    courseFields.append(
      session.selectedCourses.map((x) => {
        const ex = courses.find(
          (c) => c.event && c.event.id === (x.event && x.event.id)
        );
        return {
          ...x,
          selectedEvent: ex
            ? ex.selectedEvent
            : x.event && x.event.events && x.event.events.length > 0
            ? x.event.events[0]
            : null,
        };
      })
    );

    const ids = session.selectedCourses
      .map((x) => x.event && x.event.id)
      .filter(Boolean);

    getUpsellOnLoad(ids);

    if (session.selectedCourses.length > 0) {
      const { total } = BasketState({
        mode,
        courses: courseFields.fields,
        code,
        discount,
        points: checkoutState.points,
      });

      try {
        window.gtag &&
          window.gtag("event", "begin_checkout", {
            currency: "GBP",
            value: total / 100,
            items: session.selectedCourses.map((x, i) => {
              return {
                item_id: x.event.id,
                item_name: x.fields.title,
                affiliation: "Cosmetic College",
                currency: "GBP",
                index: i,
                item_brand: "Cosmetic College",
                item_category: "Courses",
                item_category2: x.fields.categories[0],
                item_list_id: x.fields.categories[0],
                item_list_name: capitalize(x.fields.categories[0]),
                price: x.package
                  ? x.event.standardPrice / 100
                  : x.selectedEvent
                  ? x.selectedEvent.price / 100
                  : x.event.standardPrice / 100,
                quantity: 1,
              };
            }),
          });
        window.fbq &&
          window.fbq("track", "InitiateCheckout", {
            value: total / 100,
            currency: "GBP",
            content_name: "Checkout",
            content_category: "product",
            content_type: "product",
            content_ids: session.selectedCourses.map((x) => x.event.id),
            num_ids: session.selectedCourses.length,
          });
      } catch (err) {
        console.log(err);
        Sentry.captureException(err);
      }
    }
  }, [session.selectedCourses]);

  const { errors } = form.formState;

  const checkUpsell = async (course, code: string) => {
    try {
      setCode(code);
      await handleAddCourse(course.event);
      const courses = [
        ...courseFields.fields.map((x) => x.event.id),
        course.event.id,
      ];
      await checkDiscount(code, courses);
    } catch (err) {
      console.log(err);
    }
  };

  const checkDiscount = async (code: string = null, courses: string[]) => {
    const el = document.getElementById("discount-code") as HTMLInputElement;
    const val = code ? code : el ? el.value : "";

    if (val && val.length > 0) {
      try {
        const res = await fetch("/api/discount-code/", {
          method: "POST",
          body: JSON.stringify({
            code: val.replace(/ /g, "").toUpperCase(),
            courses,
          }),
        });
        const data = await res.json();
        if (data && data.code && data.valid && data.active) {
          setDiscount(data);

          if (data.upsell) {
            setUpsell((s) => ({ ...s, loading: true }));
            const courseResult = await getUpsellInformation(courses);
            setUpsell((s) => ({ ...s, loading: false }));

            if (courseResult) {
              setUpsell(courseResult);
            }
          }
        } else {
          setDiscount(null);
          setUpsell(null);
        }
      } catch (err) {
        console.log(err);
        setDiscount(null);
        setUpsell(null);
        Sentry.captureException(err);
      }
    }
  };

  const handleAddCourse = async (course) => {
    const req = await fetch(`/api/course-search/?id=${course.id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!req.ok) {
      throw new Error("Network response was not ok");
    }
    const res = await req.json();

    if (!res.fields) {
      return null;
    }

    session.addCourse({
      ...res,
      selectedEvent:
        res && res.event && res.event.events && res.event.events[0]
          ? res.event.events[0]
          : null,
    });

    const courses = [
      ...courseFields.fields.map((x) => x.event && x.event.id).filter(Boolean),
      course.id,
    ];
    setUpsell((s) => ({ ...s, loading: true }));
    const courseResult = await getUpsellInformation(courses);
    setUpsell((s) => ({ ...s, loading: false }));

    if (courseResult) {
      setUpsell(courseResult);
    }

    try {
      const ev =
        res && res.event && res.event.events && res.event.events[0]
          ? res.event.events[0]
          : null;

      (window as any).fbq &&
        (window as any).fbq("track", "AddToCart", {
          value: ev ? ev.price / 100 : res.event.standardPrice / 100,
          currency: "GBP",
          content_name: res.fields.title,
          content_type: "product", // Required for Dynamic Product Ads
          content_ids: res.event.id,
        });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }

    klaviyoAnalytics(
      {
        eventId: klayEventId,
      },
      res,
      "Added Item to Basket"
    );

    googleAnalytics("checkout_item_added", currentCourses, basketData.total);
  };

  const handleRemoveCourse = async (index: number, courseId: string) => {
    const removedCourse = courseFields.fields[index];

    session.removeCourse(courseId);

    const courses = courseFields.fields
      .filter((course) => course.event && course.event.id !== courseId)
      .map((x) => x.event && x.event.id)
      .filter(Boolean);

    await checkDiscount(code, courses);
    setUpsell((s) => ({ ...s, loading: true }));
    const courseResult = await getUpsellInformation(courses);
    setUpsell((s) => ({ ...s, loading: false }));

    if (courseResult) {
      setUpsell(courseResult);
    }
    klaviyoAnalytics(
      {
        eventId: klayEventId,
      },
      removedCourse,
      "Removed Item from Basket"
    );
    googleAnalytics("checkout_item_removed", currentCourses, basketData.total);
  };

  const submit = (data) => {
    if (mode === "fee") {
      onSubmit({
        ...data,
        mode: "fee",
      });
    } else if (mode === "full") {
      onSubmit({
        ...data,
        mode: "full",
      });
    }
  };

  const onSubmit = async (data) => {
    // setCheckoutState((s) => ({ ...s, loading: true }));
    const currentMode = mode;
    try {
      const basketData = BasketState({
        mode,
        courses: currentCourses,
        code,
        discount,
        points: checkoutState.points,
      });
      const { total, fullTotal, deposit } = basketData;

      try {
        googleAnalytics("add_payment_info", currentCourses, total);

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.identify({
            $email: data.email,
            $first_name: data.name,
            $last_name: data.surname,
            $phone_number: data.phone,
          });

        const items = data.courses.map((x) => klayAction(x));
        klaviyoAnalytics(
          {
            $email: data.email,
            $payment_method: data.selected,
            Items: items,
            eventId: klayEventId,
          },
          null,
          "Attempted payment"
        );

        window.fbq &&
          window.fbq("track", "AddPaymentInfo", {
            currency: "GBP",
            content_ids: currentCourses.map((x) => x.event.id),
            content_category: "product",
            content_type: "product", // Required for Dynamic Product Ads
            contents: currentCourses.map((x) => ({
              id: x.event.id,
              quantity: 1,
            })),
            value: fullTotal / 100,
          });
      } catch (err) {
        Sentry.captureException(err);
        console.log(err);
      }

      const events = data.courses.map((field) => {
        const pricingItem =
          field.fields &&
          field.includeKit !== "false" &&
          field.fields.content.find(
            (x) => x.sys.contentType.sys.id === "pricing"
          );
        return field.selectedEvent
          ? {
              ...field.selectedEvent,
              seat_price: field.selectedEvent.price,
              kit:
                pricingItem &&
                pricingItem.fields &&
                pricingItem.fields.kitPrice &&
                pricingItem.fields.kitName
                  ? {
                      kitPrice: pricingItem.fields.kitPrice * 100,
                      kitName: pricingItem.fields.kitName,
                    }
                  : pricingItem &&
                    pricingItem.fields &&
                    pricingItem.fields.freeKit
                  ? {
                      kitPrice: 0,
                      kitName: "Free Kit",
                    }
                  : undefined,
            }
          : {
              id: field.event.id,
              created_at: new Date(),
              updated_at: new Date(),
              price: field.event.standardPrice,
              seat_price: field.event.standardPrice,
              seats: 0,
              type: "onsite",
              start: new Date(),
              end: new Date(),
              date: new Date(),
              detail: {
                title: field.fields.title,
                is_package: field.event.package,
                e_learning_course_id: null,
                id: field.event.id,
              },
              status: "NEW",
              detail_id: field.event.id,
              registrations: [],
              countStudentRegistrations: {
                aggregate: {
                  count: 0,
                },
              },
              kit:
                pricingItem && pricingItem.fields && pricingItem.fields.kitPrice
                  ? {
                      kitPrice: pricingItem.fields.kitPrice * 100,
                      kitName: pricingItem.fields.kitName,
                    }
                  : null,
            };
      });

      const amountToPay =
        currentMode === "full" || basketData.discount > 0
          ? fullTotal
          : basketData.deposit;

      const balance = fullTotal - amountToPay;

      const enrollment = {
        enrollment: {
          balance,
          metadata: events,
          deposit: data.mode === "fee" ? deposit : 0,
          deposit_required: data.mode === "fee",
          total: fullTotal,
          method: "stripe",
          status: "pending",
          type: "student",
          organization: process.env.NEXT_PUBLIC_ORGANIZATION_ID,
          referral: null,
        },
        discount: null,
        metadata: data,
      };

      if (referral) {
        enrollment.enrollment.referral = {
          data: [
            {
              referral_id: referral.id,
            },
          ],
        };
      }

      if (
        (discount && discount.id && basketData.discount > 0) ||
        (discount && discount.id && discount.code === "CPD999")
      ) {
        enrollment.discount = discount;
        enrollment.metadata = {
          ...enrollment.metadata,
          discount,
        };
      }

      const newEnrollmentId = await createEnrollment(enrollment);

      const intentRes = await updateIntent(
        intentState.id,
        amountToPay,
        undefined,
        data.email,
        {
          "Enrollment ID": newEnrollmentId,
          name: data.name,
          surname: data.surname,
          phone: data.phone,
          email: data.email,
          type: currentMode === "full" ? "full-amount" : "booking-fee",
        }
      );

      console.log("intentRes", intentRes);

      if (!internalRef.current) {
        throw new Error("Cannot access stripe internals");
      }

      if (!internalRef.current.elements) {
        throw new Error("Cannot access stripe elements");
      }

      const { error } = await internalRef.current.stripe.confirmPayment({
        elements: internalRef.current.elements,
        confirmParams: {
          return_url:
            process.env.NODE_ENV === "development"
              ? `http://localhost:3000/enrolment/${newEnrollmentId}`
              : `https://www.cosmetic.college/enrolment/${newEnrollmentId}`,
        },
      });

      if (error && error.message) {
        throw new Error(`Payment failed ${error.message}`);
      } else {
        localStorage.removeItem(REFERRAL_CODE_KEY);
      }
      setCheckoutState((s) => ({ ...s, loading: false, success: true }));
    } catch (err) {
      Sentry.captureException(err);
      (window as any).heap &&
        (window as any).heap.track("Booking Error", {
          method: "Enrol Error",
          ...err,
        });
      console.log(err);
      if (err && err.message) {
        setError(`Payment failed ${err.message}`);
      } else {
        setError("Payment failed");
      }
      setCheckoutState((s) => ({ ...s, loading: false }));
    }
  };

  const canPayFee = currentCourses.every(
    (course) =>
      course.selectedEvent &&
      isAfter(new Date(course.selectedEvent.start), addDays(new Date(), 14))
  );

  console.log("courses", session.selectedCourses);

  return (
    <>
      <Head>
        <title>Level Up Your Career. Enrol On Training!</title>
        <meta
          name="description"
          content="Train with industry experts that have over 30 years of industry and educational experience providing highly curated curriculums of beauty and aesthetics training"
        />
        <link rel="canonical" href="https://www.cosmetic.college/checkout/" />
        <link
          rel="alternate"
          hrefLang="en"
          href="https://www.cosmetic.college/checkout/"
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://www.cosmetic.college/checkout/"
        />
      </Head>
      <form onSubmit={form.handleSubmit(submit)}>
        <div className="bg-white">
          <div
            className="fixed left-0 top-0 hidden h-full w-1/2 bg-white lg:block"
            aria-hidden="true"
          />
          <div
            className="fixed right-0 top-0 hidden h-full w-1/2 bg-brand-500 lg:block"
            aria-hidden="true"
          />

          <header className="relative mx-auto max-w-7xl bg-brand-600 py-6 lg:grid lg:grid-cols-2 lg:gap-x-16 lg:bg-transparent lg:px-8 lg:pb-10 lg:pt-16">
            <div className="mx-auto flex max-w-2xl px-4 lg:w-full lg:max-w-lg lg:px-0">
              <Link href="/">
                <span className="sr-only">Cosmetic College</span>
                <Image
                  src="/logo/cc-logo.png"
                  alt="The Cosmetic College"
                  width={185}
                  height={45}
                  className="hidden lg:block"
                  // fill
                />
                <Image
                  src="/logo/cc-logo-white.png"
                  alt="The Cosmetic College"
                  width={185}
                  height={45}
                  className="block lg:hidden"
                  // fill
                />
              </Link>
            </div>
          </header>

          <main className="relative mx-auto grid max-w-7xl grid-cols-1 gap-x-16 lg:grid-cols-2 lg:px-8">
            <h1 className="sr-only">Checkout</h1>

            {loading ? (
              <Loading code={code} />
            ) : (
              <section
                aria-labelledby="summary-heading"
                className="bg-brand-600 pb-6 pt-6 text-brand-300 md:px-10 lg:col-start-2 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg lg:bg-transparent lg:px-0 lg:pb-24 lg:pt-0"
              >
                <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
                  <h2 id="summary-heading" className="sr-only">
                    Order summary
                  </h2>

                  {bulk && discount && discount.value && discount?.code && (
                    <div className="flex justify-between mb-5 bg-white shadow-sm p-4 border-gray-200 border rounded-lg">
                      <dl>
                        <dt className="text-sm font-medium text-gray-700">
                          Your eligible for a discount using code{" "}
                          <span className="font-semibold">
                            &quot;
                            {discount?.code}&quot;
                          </span>{" "}
                          for{" "}
                          {discount?.type === "percent"
                            ? `${discount.value}%`
                            : `${currency(discount.value)}`}{" "}
                          off your training.
                        </dt>
                        {discount.description && (
                          <dt className="text-sm font-light text-gray-600 mt-1">
                            {discount.description}
                          </dt>
                        )}
                      </dl>
                    </div>
                  )}

                  {basketData.total > 0 ? (
                    <div className="flex justify-between">
                      <dl>
                        <dt className="text-sm font-medium text-white">
                          Your Training Total
                        </dt>
                        <dd className="mt-1 text-3xl font-bold tracking-normal text-white tabular-nums">
                          {currency(basketData.total)}
                        </dd>
                      </dl>
                      <div>
                        <button
                          type="button"
                          onClick={() => setSearching(true)}
                          className="rounded-md bg-gray-100 py-2 px-4 text-sm font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50 transition-colors duration-100 flex items-center justify-center"
                        >
                          <PlusIcon className="h-4 w-4 text-gray-500 mr-1" />
                          Add a course
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {courseFields.fields.length > 0 ? (
                    <>
                      <ul
                        role="list"
                        className="divide-y divide-white divide-opacity-50 text-sm font-medium"
                      >
                        {courseFields.fields.map((course, i) => {
                          const discountedItem =
                            basketData.courses && course.event
                              ? basketData.courses.find(
                                  (x) =>
                                    x.event && x.event.id === course.event.id
                                )
                              : null;

                          return (
                            <ManualUpsell
                              key={`${course.id}-${i}`}
                              courses={courseFields.fields}
                              session={session}
                              course={course}
                              courseData={props.courseData}
                              handleAddCourse={handleAddCourse}
                              checkDiscount={checkDiscount}
                              setCode={setCode}
                              form={form}
                            >
                              <CourseItem
                                course={course}
                                form={form}
                                index={i}
                                update={(v) => courseFields.update(i, v)}
                                remove={() => {
                                  handleRemoveCourse(i, course.event.id);
                                }}
                                discountedItem={discountedItem}
                              />
                            </ManualUpsell>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center justify-between w-full">
                          <h3 className="text-lg font-medium text-white">
                            Popular Courses
                          </h3>
                          <button
                            type="button"
                            onClick={() => setSearching(true)}
                            className="rounded-md bg-gray-100 py-2 px-4 text-sm font-medium text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50 transition-colors duration-100 flex items-center justify-center"
                          >
                            <PlusIcon className="h-4 w-4 text-gray-500 mr-1" />
                            Add a course
                          </button>
                        </div>
                      </div>
                      <ul role="list" className="mt-4 grid grid-cols-1 gap-4">
                        {props.popularCourses.map((course, courseIdx) => {
                          return (
                            <li key={courseIdx}>
                              <button
                                type="button"
                                onClick={() => handleAddCourse(course)}
                                className="group flex w-full items-center justify-between space-x-3 rounded-lg border border-gray-300 p-2 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 bg-gray-100"
                              >
                                <span className="flex min-w-0 flex-1 items-center space-x-3">
                                  <span className="block flex-shrink-0">
                                    <img
                                      className="h-10 w-10 rounded-lg"
                                      src={
                                        course && course.image_link
                                          ? course.image_link
                                          : ""
                                      }
                                      alt=""
                                    />
                                  </span>
                                  <span className="block truncate text-sm font-medium text-gray-900">
                                    {course?.title ? course.title : ""}
                                  </span>
                                </span>
                                <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center">
                                  <PlusIcon
                                    className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {basketData.total > 0 ? (
                    <>
                      <DiscountCode
                        discount={discount}
                        setDiscount={setDiscount}
                        setUpsell={setUpsell}
                        courses={courseFields.fields}
                        code={code}
                        inputRef={inputRef}
                        submitRef={submitRef}
                      />

                      {upsell ? (
                        <Upsell
                          data={upsell}
                          discountValue={basketData && basketData.discount}
                          checkUpsell={checkUpsell}
                          courses={courseFields.fields}
                          inputRef={inputRef}
                          submitRef={submitRef}
                          setSearching={setSearching}
                          setUpsellMode={setUpsellMode}
                        />
                      ) : null}

                      {session?.userData?.points ? (
                        <YourRewards
                          points={session?.userData?.points}
                          setPoints={(points) =>
                            setCheckoutState((s) => ({ ...s, points }))
                          }
                          show={
                            !!(session?.userData && session.userData.points > 0)
                          }
                        />
                      ) : null}

                      <dl className="space-y-6 mt-6 border-t border-white border-opacity-50 pt-6 text-sm font-medium text-white">
                        <div className="flex items-center justify-between">
                          <dt>Subtotal</dt>
                          <dd className="tabular-nums">
                            {currency(basketData.total)}
                          </dd>
                        </div>

                        {!!checkoutState.points ? (
                          <div className="flex items-center justify-between">
                            <dt>Rewards</dt>
                            <dd className="tabular-nums">
                              {currency(checkoutState.points)}
                            </dd>
                          </div>
                        ) : null}

                        {basketData.discount ? (
                          <div className="flex items-center justify-between">
                            <dt>Discount</dt>
                            <dd className="tabular-nums">
                              {currency(basketData.discount)}
                            </dd>
                          </div>
                        ) : null}

                        <div className="flex items-center justify-between">
                          <dt>VAT (20%)</dt>
                          <dd className="tabular-nums">
                            {currency(basketData.vat)}
                          </dd>
                        </div>

                        <div className="flex items-center justify-between border-t border-white border-opacity-50 pt-6 text-white">
                          <dt className="text-base">Full Training Total</dt>
                          <dd className="text-base tabular-nums">
                            {currency(basketData.fullTotal)}
                          </dd>
                        </div>

                        {mode === "fee" && !(basketData.discount > 0) && (
                          <div className="flex items-center justify-between border-t border-white border-opacity-50 pt-6 text-white">
                            <dt className="text-base">
                              Amount To Be Paid Today
                            </dt>
                            <dd className="text-base tabular-nums">
                              {currency(basketData.deposit)}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </>
                  ) : null}
                </div>

                {basketData.total > 0 ? (
                  <div className="hidden lg:inline border-t border-white border-opacity-50 pt-6  lg:mt-5 ">
                    <div className="flex flex-row items-start space-x-3 w-full my-5 bg-white p-2 rounded-md shadow">
                      <input
                        id="terms-checkbox"
                        type="checkbox"
                        checked={agreed}
                        className="h-5 w-5 text-brand-600 focus:ring-brand-500 border-gray-300 rounded mt-1"
                        onClick={() => setAgreed(!agreed)}
                      />
                      <label
                        className="text-gray-700 text-sm font-medium"
                        htmlFor="terms-checkbox"
                      >
                        By placing this booking I confirm I understand and agree
                        to the{" "}
                        <span className="hover:text-gray-500 hover:underline decoration-gray-500">
                          <a
                            {...termsProps}
                            className="underline text-brand-600"
                            target="_blank"
                          >
                            terms and conditions
                          </a>
                        </span>{" "}
                        relating to my booking training with the Cosmetic
                        College
                      </label>
                    </div>

                    <div className="lg:flex space-x-2 justify-end">
                      <div id="snap-uk-checkout"></div>
                      {/* <button type="button" onClick={() => andSnap()}>And Snap</button> */}
                      {basketData.total && basketData.total >= 100000 ? (
                        <button
                          type="button"
                          disabled={checkoutState.loading || !agreed}
                          onClick={() =>
                            setFinance((s) => ({ ...s, finance: !s.finance }))
                          }
                          className="rounded-md border border-transparent disabled:opacity-60 bg-gray-100 px-6 py-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                        >
                          Finance Your Training
                        </button>
                      ) : null}
                      <button
                        type="submit"
                        disabled={checkoutState.loading || !agreed}
                        className="rounded-md border border-transparent disabled:opacity-50 bg-white px-6 py-4 text-sm font-medium text-brand-600 shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                      >
                        Pay now
                      </button>
                    </div>
                  </div>
                ) : null}
              </section>
            )}

            {courseFields.fields &&
            courseFields.fields.length > 0 &&
            !(code && loading) ? (
              <section
                aria-labelledby="payment-and-shipping-heading"
                className="pt-6 pb-16 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg lg:pb-24 lg:pt-0"
              >
                <h2 id="payment-and-shipping-heading" className="sr-only">
                  Payment details
                </h2>

                <div className="mx-auto max-w-2xl px-4 lg:max-w-none lg:px-0">
                  <div>
                    <h3
                      id="contact-info-heading"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Contact information
                    </h3>

                    <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="name"
                            id="name"
                            {...form.register("name")}
                            autoComplete="given-name"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            placeholder="Your name"
                          />
                          {errors.name && (
                            <span className="text-sm text-red-700">
                              Name is required
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="surname"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="surname"
                            name="surname"
                            autoComplete="family-name"
                            {...form.register("surname")}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            placeholder="Last name"
                          />
                          {errors.surname && (
                            <span className="text-sm text-red-700">
                              Surname is required
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      <div className="">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email address
                        </label>
                        <div className="mt-1">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            autoComplete="email"
                            {...form.register("email")}
                            placeholder="you@example.com"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                          />
                          {errors.email && (
                            <span className="text-sm text-red-700">
                              Email is required
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="">
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone number
                        </label>
                        <div className="mt-1">
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            autoComplete="phone_number"
                            {...form.register("phone")}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            placeholder="Phone number"
                          />
                          {errors.phone && (
                            <span className="text-sm text-red-700">
                              Phone number is required
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3
                      id="payment-heading"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Payment details
                    </h3>

                    {basketData.discount > 0 ? null : canPayFee ? (
                      <div className="border-t border-white border-opacity-10">
                        <RadioGroup value={mode} onChange={(e) => setMode(e)}>
                          <RadioGroup.Label className="text-lg font-medium text-gray-900"></RadioGroup.Label>

                          <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                            {modes.map((deliveryMethod) => (
                              <RadioGroup.Option
                                key={deliveryMethod.id}
                                value={deliveryMethod.value}
                                className={({ checked, active }) =>
                                  classNames(
                                    checked
                                      ? "border-transparent"
                                      : "border-gray-300",
                                    active ? "ring-2 ring-brand-500" : "",
                                    "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                                  )
                                }
                              >
                                {({ checked, active }) => (
                                  <>
                                    <span className="flex flex-1">
                                      <span className="flex flex-col">
                                        <RadioGroup.Label
                                          as="span"
                                          className="block text-sm font-medium text-gray-900"
                                        >
                                          {deliveryMethod.title}
                                        </RadioGroup.Label>
                                        <RadioGroup.Description
                                          as="span"
                                          className="mt-1 flex items-center text-xs text-gray-500 flex-1"
                                        >
                                          {deliveryMethod.turnaround(
                                            currency(
                                              basketData.fullTotal -
                                                basketData.deposit
                                            )
                                          )}
                                        </RadioGroup.Description>
                                        <RadioGroup.Description
                                          as="span"
                                          className="mt-6 text-sm font-medium text-gray-900"
                                        >
                                          {deliveryMethod.id === 1
                                            ? currency(basketData.deposit)
                                            : currency(basketData.fullTotal)}
                                        </RadioGroup.Description>
                                      </span>
                                    </span>
                                    {checked ? (
                                      <CheckCircleIcon
                                        className="h-5 w-5 text-brand-600"
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                    <span
                                      className={classNames(
                                        active ? "border" : "border-2",
                                        checked
                                          ? "border-brand-500"
                                          : "border-transparent",
                                        "pointer-events-none absolute -inset-px rounded-lg"
                                      )}
                                      aria-hidden="true"
                                    />
                                  </>
                                )}
                              </RadioGroup.Option>
                            ))}
                          </div>
                        </RadioGroup>
                      </div>
                    ) : null}

                    {intentState.client_secret ? (
                      <div className="mt-6">
                        <Pay
                          internalRef={internalRef}
                          client_secret={intentState.client_secret}
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-10 flex flex-col border-t border-gray-200 pt-6 lg:hidden">
                    <div className="flex flex-row items-start space-x-3 w-full my-5 bg-white p-2 rounded-md shadow">
                      <input
                        id="terms-checkbox2"
                        type="checkbox"
                        checked={agreed}
                        className="h-5 w-5 text-brand-600 focus:ring-brand-500 border-gray-300 rounded mt-1"
                        onClick={() => setAgreed(!agreed)}
                      />
                      <label
                        className="text-gray-700 text-sm font-medium"
                        htmlFor="terms-checkbox2"
                      >
                        By placing this booking I confirm I understand and agree
                        to the{" "}
                        <span className="hover:text-gray-500 hover:underline decoration-gray-500">
                          <a
                            {...termsProps}
                            className="underline text-brand-600"
                            target="_blank"
                          >
                            terms and conditions
                          </a>
                        </span>{" "}
                        relating to my booking training with the Cosmetic
                        College
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={checkoutState.loading || !agreed}
                      className="w-full rounded-md border border-transparent disabled:opacity-50 bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                    >
                      Pay now
                    </button>
                  </div>

                  {basketData.total && basketData.total >= 100000 ? (
                    <div className="flex lg:hidden">
                      <button
                        type="button"
                        disabled={checkoutState.loading || !agreed}
                        onClick={() =>
                          setFinance((s) => ({ ...s, finance: !s.finance }))
                        }
                        className="mt-6 group  disabled:opacity-50 relative w-full inline-flex items-center justify-center px-6 py-3 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors duration-100"
                      >
                        Finance Your Training
                      </button>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {(courseFields.fields && courseFields.fields.length === 0) ||
            !courseFields.fields ||
            (code && loading) ? (
              <section
                aria-labelledby="payment-and-shipping-heading"
                className="py-16 flex flex-col justify-center items-center  lg:col-start-1 lg:row-start-1 lg:mx-auto lg:w-full lg:max-w-lg lg:pb-24 lg:pt-0"
              >
                <EmptySvg />
                <h2 className="mt-2 text-base font-semibold leading-6 text-gray-900">
                  No Courses
                </h2>
                <p className="mt-1 px-3 text-center lg:text-left lg:px-0 text-sm text-gray-500 mb-4">
                  You have no courses in your basket. Please add a course!
                </p>
                <div className="flex items-center gap-2">
                  <Link
                    href="/courses"
                    className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-500 hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-brand-500"
                  >
                    Browse Courses
                  </Link>
                </div>
              </section>
            ) : null}
          </main>
        </div>
      </form>

      <Modal
        title="Add A Course"
        open={searching}
        xlarge
        close={() => {
          setUpsellMode(false);
          setSearching(false);
        }}
      >
        <div className="overflow-y-scroll h-full">
          <h3 className="text-lg font-medium text-white">Popular Courses</h3>
          {bulk && (
            <div className="flex items-center">
              <Status value="ELIGIBLE" />
              <div className="w-2 h-[1px] mx-2 bg-gray-700" />
              {discount && discount.code && (
                <p className="text-sm font-medium text-gray-700">
                  Courses eligible for discount &quot;{discount.code}&quot;
                </p>
              )}
            </div>
          )}
          <ul role="list" className="mt-4 grid grid-cols-1 gap-4">
            {props.courseData
              .sort((a, b) => {
                if (!bulk) {
                  if (a.title < b.title) {
                    return -1;
                  }
                  if (a.title > b.title) {
                    return 1;
                  }
                  return 0;
                }
                const aEligible = discount && discount.applyFrom.includes(a.id);
                const bEligible = discount && discount.applyFrom.includes(b.id);
                if (aEligible === bEligible) {
                  return 0;
                } else if (aEligible) {
                  return -1;
                } else {
                  return 1;
                }
              })
              .map((course, courseIdx) => {
                const eligible =
                  bulk && discount && discount.applyFrom.includes(course.id);
                // If in upsell mode, skip all courses that would not be valid for this discount.
                if (
                  upsellMode &&
                  upsell &&
                  upsell.loading === false &&
                  upsell.allRemainingValidCourses &&
                  !upsell.allRemainingValidCourses.includes(course.id)
                ) {
                  return;
                }

                return (
                  <li key={courseIdx}>
                    <button
                      type="button"
                      onClick={() => {
                        handleAddCourse(course);
                        setSearching(false);
                      }}
                      className="group flex w-full items-center justify-between space-x-3 rounded-lg border border-gray-300 p-2 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 bg-gray-100"
                    >
                      <span className="flex min-w-0 flex-1 items-center space-x-3">
                        <span className="block flex-shrink-0">
                          <img
                            className="h-10 w-10 rounded-lg"
                            src={
                              course && course.image_link
                                ? course.image_link
                                : ""
                            }
                            alt=""
                          />
                        </span>
                        <span className="block truncate text-sm font-medium text-gray-900">
                          {course?.title ? course.title : ""}
                        </span>
                      </span>
                      {eligible && <Status value="ELIGIBLE" />}
                      <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center">
                        <PlusIcon
                          className="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      </Modal>
      <Modal
        close={() => setFinance((s) => ({ ...s, finance: false }))}
        open={finance.finance}
        full
      >
        {finance.done && (
          <div className="flex flex-col items-center h-full">
            <div className="p-6 px-12 border rounded">
              <p className="mb-1 text-left font-extrabold tracking-tight text-xl px-4">
                <span>Details Received</span>
              </p>
              <p className="mt-4 px-4">
                We have sucessfully received your details and this has been
                passed on to the team. The team aim to respond within 2 hours of
                submission during business hours.
              </p>
              <div className="flex justify-end">
                <div className="w-60 px-4 mt-6">
                  <button
                    onClick={() =>
                      setFinance((s) => ({ ...s, finance: false }))
                    }
                    className="group relative w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow text-base font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors duration-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {!finance.done && (
          <div className="flex flex-col h-full overflow-scroll">
            <div className="bg-white shadow">
              <div className="border border-gray-100 rounded-md mx-auto divide-y divide-gray-200 lg:flex lg:justify-center lg:divide-y-0 lg:divide-x">
                {perks.map((perk, perkIdx) => (
                  <div
                    key={perkIdx}
                    className="py-4 md:py-8 lg:w-1/3 lg:flex-none lg:py-4"
                  >
                    <div className="mx-auto flex max-w-xs items-center px-4 lg:max-w-none lg:px-8">
                      <perk.icon
                        className="h-8 w-8 flex-shrink-0 text-brand-600"
                        aria-hidden="true"
                      />
                      <div className="ml-4 flex flex-auto flex-col">
                        <h3 className="font-medium text-gray-900">
                          {perk.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {perk.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex-1 w-full mx-auto lg:py-8 flex flex-col">
              <div className="shadow-slate-300 pt-4 md:pt-4 flex flex-col border rounded-md">
                <h4 className="mb-1 text-left font-extrabold tracking-tight text-xl px-4">
                  Finance Your Training
                </h4>
                <div className="flex-1 pt-2">
                  <div className="px-4">
                    <p>
                      Enrol in complete training programmes whilst spreading
                      your investment costs, allowing you to focus on growing
                      your skills and career with the Cosmetic College.
                    </p>
                    <p className="mt-2">
                      Complete the finance application form to get the process
                      started. Once received one a member of our finance team
                      will be in touch to work with you to find the right
                      training and payment programme.
                    </p>
                  </div>
                  <div>
                    <form onSubmit={financeForm.handleSubmit(financeSubmit)}>
                      <div className="overflow-hidden shadow sm:rounded-md">
                        <div className="bg-white py-5 sm:py-6 px-4">
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="first-name"
                                className="block text-sm font-medium text-gray-700"
                              >
                                First name
                              </label>
                              <input
                                type="text"
                                name="first-name"
                                id="first-name"
                                autoComplete="given-name"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                {...financeForm.register("first_name", {
                                  required: true,
                                })}
                              />
                              {financeForm.formState.errors.first_name && (
                                <span className="text-red-600 text-sm">
                                  First name is required
                                </span>
                              )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="last-name"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Last name
                              </label>
                              <input
                                type="text"
                                name="last-name"
                                id="last-name"
                                autoComplete="family-name"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                {...financeForm.register("last_name", {
                                  required: true,
                                })}
                              />
                              {financeForm.formState.errors.last_name && (
                                <span className="text-red-600 text-sm">
                                  Last name is required
                                </span>
                              )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="email-address"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Email address
                              </label>
                              <input
                                type="text"
                                name="email-address"
                                id="email-address"
                                autoComplete="email"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                {...financeForm.register("email", {
                                  required: true,
                                })}
                              />
                              {financeForm.formState.errors.email && (
                                <span className="text-red-600 text-sm">
                                  Email address is required
                                </span>
                              )}
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="contact-number"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Phone number
                              </label>
                              <input
                                type="text"
                                id="contact-number"
                                autoComplete="contact-number"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                                {...financeForm.register("phone_number", {
                                  required: true,
                                })}
                              />
                              {financeForm.formState.errors.phone_number && (
                                <span className="text-red-600 text-sm">
                                  Phone number is required
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 text-right sm:px-6 flex md:justify-end">
                          <div className="w-full md:w-60">
                            <button
                              type="submit"
                              className="group relative w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow text-base font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors duration-100"
                            >
                              Start Application
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function Loading({ code }: { code: string }) {
  return (
    <div className="bg-brand-600 pb-12 pt-8 lg:p-0 text-center text-white md:px-10 lg:col-start-2 row-start-1 lg:mx-auto w-full lg:max-w-md lg:bg-transparent lg:px-0 lg:pb-24 ">
      <Image
        src="/logo/cc-logo-white.png"
        alt="The Cosmetic College"
        width={185}
        height={45}
        className="hidden lg:inline pb-5"
      />
      <div className="py-1">
        {code ? (
          <>
            <p className="text-lg font-semibold">Found Discount Code:</p>
            <div className="bg-white px-3 py-1.5 rounded-md w-fit mx-auto my-2">
              <p className="font-semibold text-sm text-center text-gray-600 ">
                &quot;{code.toUpperCase()}&quot;
              </p>
            </div>
            <p className="text-base font-medium pt-1">
              Please wait while we load your courses
            </p>
          </>
        ) : (
          <p className="text-lg font-semibold">Loading...</p>
        )}
        <Loader stroke="#fff" width={100} height={100} styles="pt-10 lg:pt-4" />
      </div>
    </div>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const siteData = await loadStandardContent();
  const req = await fetch(
    // "http://localhost:3000/api/catalog?courses=true&start"
    `https://cosmetic.college/api/catalog?courses=true&start`
  );
  const res = await req.json();

  // Generate 6 random course indexes, ensuring no duplicates.
  const numbers = new Set();
  const popularCourses = [];

  while (popularCourses.length < 6) {
    const number = Math.floor(Math.random() * res.length);
    if (!numbers.has(number)) {
      numbers.add(number);
      popularCourses.push(res[number]);
    }
  }

  return {
    props: {
      siteData,
      courseData: res,
      popularCourses,
    },
  };
}

const appearance: Appearance = {
  theme: "flat",
  variables: {
    fontFamily: "Montserrat, Arial, sans-serif",
    fontLineHeight: "1.5",
    borderRadius: "8px",
    colorBackground: "#fff",
    spacingUnit: "6px",
  },
  rules: {
    ".Block": {
      border: "none",
    },
    ".Error": {
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "16px",
      color: "rgba(220, 38, 38, 1)",
      marginTop: "8px",
    },
    ".Input": {
      fontSize: "14px",
      fontWeight: "400",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "rgba(224, 224, 224, 1)",
      boxShadow:
        "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      paddingLeft: "12px",
      paddingRight: "12px",
      paddingTop: "8px",
      paddingBottom: "8px",
      outline: "none",
      transition: "none",
      lineHeight: "20px",
      color: "#374151",
    },
    ".Input:focus": {
      borderWidth: "2px",
      borderStyle: "solid",
      borderColor: "rgb(12, 148, 152, 1)",
      transition: "none",
    },
    ".Input:disabled, .Input--invalid:disabled": {
      color: "lightgray",
      transition: "none",
    },
    ".Input--invalid": {
      color: "rgba(249, 128, 128, 1)",
    },
    ".Tab": {
      padding: "10px 12px 8px 12px",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "rgba(224, 224, 224, 1)",
      boxShadow:
        "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      transition: "none",
    },
    ".Tab:hover": {
      boxShadow:
        "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      transition: "none",
    },
    ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
      borderWidth: "2px",
      borderStyle: "solid",
      borderColor: "rgb(12, 148, 152, 1)",
      backgroundColor: "#fff",
      boxShadow:
        "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      color: "#262626",
      transition: "none",
    },
    ".Label": {
      fontWeight: "500",
      color: "#374151",
      fontSize: "0.875rem",
    },
    ".RedirectText": {
      fontWeight: "500",
      color: "#374151",
    },
  },
};

async function createIntent(
  amount: number,
  customerId?: string | null | undefined,
  email?: string | null | undefined
): Promise<{
  id: string;
  client_secret: string;
  amount: number;
} | null> {
  const req = await fetch("/api/checkout/", {
    method: "post",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      amount: amount,
      customerId,
      email,
    }),
  });
  const json = await req.json();
  return json;
}

async function updateIntent(
  intent: string,
  amount: number,
  customerId?: string | null | undefined,
  email?: string | null | undefined,
  data?: any
): Promise<{
  id: string;
  client_secret: string;
  amount: number;
} | null> {
  const req = await fetch("/api/checkout/", {
    method: "put",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      intent,
      amount: amount,
      customerId,
      email,
      metadata: data,
    }),
  });
  const json = await req.json();
  return json;
}

async function createEnrollment(data: any) {
  let cookieData = {};
  try {
    cookieData = JSON.parse(decodeURIComponent(cookies.get("ccmgd")));
  } catch (err) {
    console.log(err);
  }
  const req = await fetch("/api/enroll/", {
    method: "post",
    body: JSON.stringify({
      data: {
        ...data,
        metadata: {
          ...cookieData,
          // ...(session.sourceParams || {}),
          ...data.metadata,
        },
      },
    }),
  });

  const res: { id: string } = await req.json();
  localStorage.setItem("cc.orderId2", res.id);

  return res.id;
}

const getUpsellInformation = async (courses: string[]) => {
  try {
    console.log("LOOKING UPSELL", courses);

    const upsellReq = await fetch(`/api/upsell/`, {
      method: "POST",
      body: JSON.stringify({
        courses,
      }),
    });

    if (upsellReq.ok) {
      const upsellRes = await upsellReq.json();
      console.log("upsellRes", upsellRes);

      // Keep a list of all valid discount options (that aren't already in the basket).
      if (upsellRes.discount) {
        switch (upsellRes.discount.config.condition) {
          case "bulkDiscount":
            upsellRes.allRemainingValidCourses = [
              ...upsellRes.remainingApplyFrom,
            ];
            break;
          case "buyXGetY":
            upsellRes.allRemainingValidCourses = [
              ...upsellRes.remainingApplyTo,
            ];
            break;
        }
      }

      const remainingCoursesFrom =
        upsellRes.remainingApplyFrom &&
        (await Promise.all(
          upsellRes.remainingApplyFrom.splice(0, 4).map(async (id) => {
            const req = await fetch(`/api/course-search/?id=${id}`, {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            });
            const res = await req.json();
            return res;
          })
        ));

      const remainingCoursesTo =
        upsellRes.remainingApplyTo &&
        (await Promise.all(
          upsellRes.remainingApplyTo.splice(0, 4).map(async (id) => {
            const req = await fetch(`/api/course-search/?id=${id}`, {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            });
            const res = await req.json();
            return res;
          })
        ));

      upsellRes.remainingCoursesFrom = remainingCoursesFrom ?? [];
      upsellRes.remainingCoursesTo = remainingCoursesTo ?? [];

      return upsellRes;
    }
  } catch (err) {
    console.log(err);
    Sentry.captureException(err);
    return null;
  }
};

const modes = [
  {
    id: 1,
    title: "Pay Booking Fee",
    turnaround: (e) => `${e} due 14 days before start date`,
    value: "fee",
  },
  {
    id: 2,
    title: "Pay Training Total",
    turnaround: (e) => "Get started straight away",
    value: "full",
  },
];

const perks = [
  {
    name: "Flexible payment options",
    description: "All credit types considered",
    icon: CalendarIcon,
  },
  {
    name: "Grow your skills",
    description: "0% interest available",
    icon: ArrowPathIcon,
  },
  {
    name: "Control your investment",
    description: "0-18 month terms",
    icon: AcademicCapIcon,
  },
];

const klaviyoAnalytics = (
  obj: { [key: string]: any },
  course: any,
  description: string
) => {
  try {
    const item = course ? klayAction(course) : undefined;
    // @ts-ignore
    klaviyo &&
      // @ts-ignore
      klaviyo.push([
        "track",
        description,
        {
          Course: item,
          ...obj,
        },
      ]);
  } catch (err) {
    console.log(err);
    Sentry.captureException(err);
  }
};

const googleAnalytics = (
  description: string,
  currentCourses: any[],
  total: number
) => {
  if (currentCourses.length > 0) {
    const items = currentCourses.map((x, i) => {
      return {
        item_id: x?.event?.id,
        item_name: x.fields.title,
        affiliation: "Cosmetic College",
        currency: "GBP",
        index: i,
        item_brand: "Cosmetic College",
        item_category: "Courses",
        item_category2: x.fields.categories[0],
        item_list_id: x.fields.categories[0],
        item_list_name: capitalize(x.fields.categories[0]),
        price: x.package
          ? x.event?.standardPrice / 100
          : x.selectedEvent
          ? x.selectedEvent.price / 100
          : x.event?.standardPrice / 100,
        quantity: 1,
      };
    });
    try {
      window.gtag &&
        window.gtag("event", description, {
          currency: "GBP",
          value: total / 100,
          items,
        });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  }
};

const termsProps = {
  href: "/terms-and-conditions/",
  rel: "noopener noreferrer",
  target: "_blank",
};

const upsells = {
  mcd: {
    id: 1,
    name: "Add VTCT Level 6 Mesotherapy & Save 15%",
    description:
      "This higher-level course focuses on the therapeutic application of skin rejuvenation techniques, delivering targeted results for a range of client concerns. Led by experienced tutors, you'll gain in-depth theoretical knowledge and hands-on practice using professional mesotherapy devices and solutions.",
    href: "#",
    price: "Upgrade for £1,593.75",
    was: "Was £1,875.00",
    status: "Preparing to ship",
    step: 3,
    date: "March 24, 2021",
    datetime: "2021-03-24",
    address: ["Floyd Miles", "7363 Cynthia Pass", "Toronto, ON N3Y 4H8"],
    email: "f•••@example.com",
    phone: "1•••••••••40",
    imageSrc:
      "https://images.ctfassets.net/eo68gvto3q0j/77bydUFFYitmohlij10t3X/5bdf8e151a8ae132a9bdecc6db6fd9d3/shutterstock_1407635186.jpg",
    imageAlt: "Add VTCT Level 6 Mesotherapy",
    title1: "Exclusive Opportunity!",
    title2: "Upgrade your Level 4 and train to Level 6!",
    code: "L4MCD",
    rate: 0.15,
  },
  lsr: {
    id: 1,
    name: "Add VTCT Level 5 Laser Tattoo Removal & Save 15%",
    description:
      "Designed to equip you with advanced knowledge and skills in laser technology for the safe and effective removal of unwanted tattoos. This highly regarded qualification not only boosts your credibility but also allows you to expand your treatment offerings in a rapidly growing market.",
    href: "#",
    price: "Upgrade for £1,593.75",
    was: "Was £1,875.00",
    status: "Preparing to ship",
    step: 2,
    date: "March 24, 2021",
    datetime: "2021-03-24",
    address: ["Floyd Miles", "7363 Cynthia Pass", "Toronto, ON N3Y 4H8"],
    email: "f•••@example.com",
    phone: "1•••••••••40",
    imageSrc:
      "https://images.ctfassets.net/eo68gvto3q0j/77bydUFFYitmohlij10t3X/5bdf8e151a8ae132a9bdecc6db6fd9d3/shutterstock_1407635186.jpg",
    imageAlt: "Add VTCT Level 5 Laser Tattoo Removal",
    title1: "Exclusive Opportunity!",
    title2: "Upgrade your Level 4 and train to Level 5!",
    code: "L5LSR",
    rate: 0.15,
  },
  l3: {
    id: 1,
    name: "Add VTCT Level 4 Diploma In Aesthetics & Save 15%",
    description:
      "Elevate your professional credentials with our VTCT (iTEC) Level 4 Diploma in Advanced Aesthetic Treatments, specialising in Microneedling, Dermaplaning and Chemical Skin Peels",
    href: "#",
    price: "Upgrade for £1,593.75",
    was: "Was £1,875.00",
    status: "Preparing to ship",
    step: 1,
    date: "March 24, 2021",
    datetime: "2021-03-24",
    address: ["Floyd Miles", "7363 Cynthia Pass", "Toronto, ON N3Y 4H8"],
    email: "f•••@example.com",
    phone: "1•••••••••40",
    imageSrc:
      "https://images.ctfassets.net/eo68gvto3q0j/77bydUFFYitmohlij10t3X/5bdf8e151a8ae132a9bdecc6db6fd9d3/shutterstock_1407635186.jpg",
    imageAlt: "Add VTCT Level 5 Laser Tattoo Removal",
    title1: "Exclusive Opportunity!",
    title2: "Upgrade your Level 3 and train to Level 4!",
    code: "L34AST",
    rate: 0.15,
  },
};

function ManualUpsell({
  course,
  courses,
  courseData,
  handleAddCourse,
  session,
  checkDiscount,
  setCode,
  form,
  children,
}) {
  console.log("ManualUpsell", courses);

  let product = null;
  let target = null;

  if (
    course &&
    course.event &&
    (course.event.id === "2d8dfa48-2295-4027-b151-f512de4b3e77" ||
      course.event.id === "3d197084-afbe-4a03-a639-f784b8d637d5")
  ) {
    product = upsells.mcd;
    // mesotherapy
    target = courseData.find(
      (x) => x.id === "200c8e39-9562-4911-9925-ad2d99c10b36"
    );
  }

  if (
    course &&
    course.event &&
    course.event.id === "ab170dd8-6132-4873-9392-f9cf87f114c0"
  ) {
    product = upsells.lsr;
    // tattoo removal
    target = courseData.find(
      (x) => x.id === "b8c50515-2c6a-4bf1-8e47-c7edaddce53f"
    );
  }

  if (
    course &&
    course.event &&
    course.event.id === "80d59648-2fc7-4ea4-b268-029aaf47e6a4"
  ) {
    product = upsells.l3;
    // diploma
    target = courseData.find(
      (x) => x.id === "3d197084-afbe-4a03-a639-f784b8d637d5"
    );
  }

  console.log("product", product);
  console.log("target", target);

  if (product && target) {
    return (
      <div className="bg-white rounded-lg text-gray-800 p-4 my-4">
        <div className="px-4 bg-brand-500 rounded-t-lg">{children}</div>
        <Disclosure
          as="section"
          className="-mt-4 w-full text-left border-b border-t border-gray-200 bg-white shadow-sm rounded-b-lg border"
        >
          <Disclosure.Button className="flex justify-between items-center w-full p-4 lg:items-start">
            <div className="text-left text-sm font-medium text-brand-800">
              <span className="block font-semibold">{product.title1}</span>
              <span>{product.title2}</span>
            </div>
            <ChevronUpIcon className="w-6 h-6 text-gray-400" />
          </Disclosure.Button>
          <Disclosure.Panel>
            <div key={product.id} className="bg-white shadow-sm">
              <div className="pb-6 lg:grid lg:grid-cols-12 lg:gap-x-8 p-4">
                <div className="sm:flex lg:col-span-12">
                  <img
                    alt={product.imageAlt}
                    src={target.image_link}
                    className="aspect-square w-full shrink-0 rounded-lg object-cover sm:size-40"
                  />

                  <div className="mt-6 sm:ml-6 sm:mt-0">
                    <h3 className="text-base font-medium text-gray-900">
                      <a href={product.href}>{product.name}</a>
                    </h3>
                    <p className="mt-2 text-xs font-normal text-gray-900">
                      Was £{target.price}
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      Upgrade for £{target.price - product.rate * target.price}
                    </p>
                    <p className="mt-3 text-sm text-gray-600 font-normal">
                      {product.description}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(
                          "discount-code"
                        ) as HTMLInputElement;
                        el.value = product.code;
                        setCode(product.code);
                        handleAddCourse(target);
                        setTimeout(() => {
                          checkDiscount(product.code, [
                            ...courses.map((x) => x.event.id),
                            target.id,
                          ]);
                        }, 500);
                      }}
                      className="mt-4 w-full rounded-md border border-transparent disabled:opacity-50 bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-gray-50"
                    >
                      Upgrade
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 px-4 py-6 sm:px-6 lg:p-8">
                <h4 className="sr-only">Status</h4>
                <p className="text-sm font-medium text-gray-900">
                  Become An Aesthetics Expert With The Cosmetic College
                </p>
                <div aria-hidden="true" className="mt-6">
                  <div className="overflow-hidden rounded-full bg-gray-200">
                    <div
                      style={{
                        width: `calc((${product.step} * 2 + 1) / 10 * 100%)`,
                      }}
                      className="h-2 rounded-full bg-brand-600"
                    />
                  </div>
                  <div className="mt-6 grid grid-cols-5 text-sm font-medium text-gray-600">
                    <div className="text-brand-600">Level 3</div>
                    <div
                      className={classNames(
                        product.step > 0 ? "text-brand-600" : "",
                        "text-center"
                      )}
                    >
                      Level 4
                    </div>
                    <div
                      className={classNames(
                        product.step > 1 ? "text-brand-600" : "",
                        "text-center"
                      )}
                    >
                      Level 5
                    </div>
                    <div
                      className={classNames(
                        product.step > 2 ? "text-brand-600" : "",
                        "text-center"
                      )}
                    >
                      Level 6
                    </div>
                    <div
                      className={classNames(
                        product.step > 3 ? "text-brand-600" : "",
                        "text-right"
                      )}
                    >
                      Level 7
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Disclosure.Panel>
        </Disclosure>
      </div>
    );
  }

  return children;
}
